/**
 * React Query IndexedDB Persister
 * Persists query cache to IndexedDB for offline support
 */
import { get, set, del } from 'idb-keyval';
import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';
import { defaultShouldDehydrateQuery, type Query } from '@tanstack/react-query';

const STORAGE_KEY = 'pasture-react-query-cache';

/**
 * Query keys that should be persisted to IndexedDB
 * These are critical for offline functionality
 */
export const PERSIST_QUERY_KEYS = [
  'ra-profiles',
  'ra-profile',
  'ra-list',
  'ra-stats',
  'shop-products',
  'shop-product',
  'shop-stats',
  'shop-categories',
  'shop-deliveries',
  'shop-low-stock',
  'laundry-nets',
  'laundry-movements',
  'laundry-stats',
] as const;

/**
 * Check if a query key should be persisted
 */
export const shouldPersistQuery = (queryKey: readonly unknown[]): boolean => {
  if (!queryKey || queryKey.length === 0) return false;
  const firstKey = queryKey[0];
  if (typeof firstKey !== 'string') return false;
  return PERSIST_QUERY_KEYS.some(key => firstKey.startsWith(key));
};

/**
 * Custom dehydrate filter to only persist critical queries.
 * Mantiene il gate ufficiale di TanStack sullo status (solo query 'success'):
 * senza, le query pending verrebbero deidratate con una Promise viva allegata
 * → DataCloneError in IndexedDB (structuredClone non clona le Promise).
 */
export const dehydrateOptions = {
  shouldDehydrateQuery: (query: Query) =>
    defaultShouldDehydrateQuery(query) && shouldPersistQuery(query.queryKey),
};

const MAX_PERSISTED_QUERIES = 200;

/**
 * Creates an IndexedDB persister for React Query
 */
export function createIndexedDBPersister(): Persister {
  return {
    persistClient: async (client: PersistedClient) => {
      try {
        // Limit number of persisted queries to prevent IndexedDB bloat
        if (client?.clientState?.queries && client.clientState.queries.length > MAX_PERSISTED_QUERIES) {
          // Keep the most recently updated queries
          client = {
            ...client,
            clientState: {
              ...client.clientState,
              queries: client.clientState.queries
                .sort((a, b) => (b.state.dataUpdatedAt || 0) - (a.state.dataUpdatedAt || 0))
                .slice(0, MAX_PERSISTED_QUERIES),
            },
          };
        }
        await set(STORAGE_KEY, client);
      } catch (error) {
        // Log singolo aggregato: elencare le query non-serializzabili con un
        // console.error per ciascuna scatenava centinaia di errori/min
        // (amplificati da performanceMonitor). Riportiamo solo le chiavi colpevoli.
        const offenders = (client?.clientState?.queries ?? [])
          .filter(query => {
            try {
              structuredClone(query);
              return false;
            } catch {
              return true;
            }
          })
          .map(query => query.queryKey);
        console.error('[QueryPersister] Error persisting client:', error, {
          nonSerializableKeys: offenders,
        });
      }
    },
    restoreClient: async () => {
      try {
        return await get<PersistedClient>(STORAGE_KEY);
      } catch (error) {
        console.error('[QueryPersister] Error restoring client:', error);
        return undefined;
      }
    },
    removeClient: async () => {
      try {
        await del(STORAGE_KEY);
      } catch (error) {
        console.error('[QueryPersister] Error removing client:', error);
      }
    },
  };
}

/**
 * Singleton persister instance
 */
export const indexedDBPersister = createIndexedDBPersister();

/**
 * Clear persisted cache - useful for logout or cache invalidation
 */
export const clearPersistedCache = async () => {
  try {
    await del(STORAGE_KEY);
    console.log('[QueryPersister] Cache cleared successfully');
  } catch (error) {
    console.error('[QueryPersister] Error clearing cache:', error);
  }
};
